import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import splinterlandsSettingsService from '../services/SplinterlandsSettingsService';
import { convertUsdToDec, convertUsdToCredits } from '../scenes/market/priceUtils';

// Create context for the land shopping cart
const LandCartContext = createContext();

// Custom hook to easily use the LandCartContext
export const useLandCart = () => useContext(LandCartContext);

// Local storage key for persistent land cart items
const LAND_CART_STORAGE_KEY = 'splinterlands_land_cart_items';

/**
 * Fetches the latest Splinterlands blockchain settings
 * This helps ensure transactions are properly synchronized with the blockchain
 * Uses cached settings service to avoid redundant API calls
 * @returns {Promise<Object>} Latest blockchain settings
 */
const getLatestBlockchainSettings = async () => {
  try {
    // Use the cached settings service instead of direct API call
    const settings = await splinterlandsSettingsService.getSettings();
    return settings;
  } catch (error) {
    return null;
  }
};

/**
 * Gets the current DEC price from PriceService
 * Used for market purchases to ensure consistent pricing
 * @returns {Promise<number>} Current DEC price in USD
 */
const getCurrentDecPrice = async () => {
  try {
    // Try to get price from local PriceService first
    const priceService = (await import('../services/PriceService')).default;
    const { prices } = priceService.getData();
    
    if (prices && prices.dec && prices.dec > 0) {
      return Number(prices.dec);
    }
    
    // Fallback to fetching from Hive Engine directly
    const response = await axios.get(
      'https://api.hive-engine.com/rpc/contracts', 
      {
        method: 'POST',
        data: {
          jsonrpc: '2.0',
          id: 1,
          method: 'findOne',
          params: {
            contract: 'market',
            table: 'metrics',
            query: { symbol: 'DEC' }
          }
        }
      }
    );
    
    if (response.data && response.data.result) {
      return Number(response.data.result.lastPrice);
    }
    
    return 0.001; // Fallback default if all else fails
  } catch (error) {
    return 0.001; // Default fallback price
  }
};

/**
 * Gets the current CREDITS price
 * @returns {Promise<number>} Current CREDITS price in USD
 */
const getCreditsPrice = async () => {
  // Credits are typically priced at a fixed rate of $1 = 1000 CREDITS
  return 0.001; // $0.001 per credit
};

/**
 * LandCartProvider component that provides global state for the land shopping cart
 * @param {object} props - React props with children
 * @returns {JSX.Element} - LandCartProvider element
 */
export const LandCartProvider = ({ children }) => {
  // State for land cart items
  const [landCartItems, setLandCartItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load land cart items from localStorage on first render
  useEffect(() => {
    const savedItems = localStorage.getItem(LAND_CART_STORAGE_KEY);
    if (savedItems) {
      try {
        setLandCartItems(JSON.parse(savedItems));
      } catch (error) {
        localStorage.removeItem(LAND_CART_STORAGE_KEY);
      }
    }
  }, []);

  // Save land cart items to localStorage on every change
  useEffect(() => {
    localStorage.setItem(LAND_CART_STORAGE_KEY, JSON.stringify(landCartItems));
  }, [landCartItems]);



  /**
   * Fetches detailed land deed information from the Splinterlands API
   * @param {string} uid - The land deed's UID
   * @returns {Promise<Object|null>} - The complete land deed object or null if error
   */
  const fetchLandDeedDetailsByUid = async (uid) => {
    try {
      // Use the land API endpoint
      const cacheBuster = Math.random().toString(36).substring(2, 15);
      const response = await axios.get(`https://vapi.splinterlands.com/land/deeds?ids=${uid}&v=${cacheBuster}`);
      
      if (response.data && response.data.data && response.data.data.deeds && response.data.data.deeds.length > 0) {
        return response.data.data.deeds[0];
      } else if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        return response.data[0];
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  /**
   * Adds a land deed to the shopping cart
   * @param {Object} landDeed - The land deed listing to add
   * @param {Object} landDetails - The details of the land deed
   */
  const addToLandCart = async (landDeed, landDetails) => {
    // Check if the land deed is already in the cart
    if (landCartItems.some(item => item.uid === landDeed.uid)) {
      return;
    }

    setLoading(true);

    try {
      // Make sure we have complete land deed information
      let fullLandData = landDeed;

      // If market_id is missing or other important fields, try to fetch them
      if (!landDeed.listingItemId) {
        // Get additional land deed information from the Splinterlands API
        const apiLandDetails = await fetchLandDeedDetailsByUid(landDeed.uid);
        
        if (apiLandDetails) {
          fullLandData = {
            ...fullLandData,
            ...apiLandDetails,
            listingItemId: apiLandDetails.market_id || apiLandDetails.listingItemId
          };
        }
      }

      // Add the land deed with all relevant information
      const cartItem = {
        ...fullLandData,
        name: landDetails?.name || fullLandData.name || `Land R${fullLandData.region_id} T${fullLandData.tract_id} P${fullLandData.plot_id}`,
        price: fullLandData.price || fullLandData.buy_price || fullLandData.listing_price,
        originalPrice: fullLandData.originalPrice || fullLandData.buy_price || fullLandData.listing_price,
        originalCurrency: fullLandData.originalCurrency || fullLandData.currency || 'USD',
        listingItemId: fullLandData.listingItemId || fullLandData.market_id, // Required for land purchases - Use market_id
        quantity: 1, // Always 1 for land deed
        type: 'land_deed', // Mark as land deed type
        timeAdded: new Date().toISOString(),
      };

      setLandCartItems(prevItems => [...prevItems, cartItem]);
    } catch (error) {
      // Silent error handling
    } finally {
      setLoading(false);
    }
  };

  /**
   * Removes a land deed from the shopping cart
   * @param {string} uid - The UID of the land deed to remove
   */
  const removeFromLandCart = (uid) => {
    setLandCartItems(prevItems => prevItems.filter(item => item.uid !== uid));
  };

  /**
   * Clears the entire land shopping cart
   */
  const clearLandCart = () => {
    setLandCartItems([]);
  };

  /**
   * Removes expired or unavailable land deeds from the cart
   */
  const cleanupLandCart = async () => {
    if (landCartItems.length === 0) return;
    
    const validItems = [];
    
    for (const item of landCartItems) {
      if (item.listingItemId) {
        // Skip availability check - keep all items with listing IDs
        validItems.push(item);
      } else {
        validItems.push(item); // Keep items without listing ID for now
      }
    }
    
    if (validItems.length !== landCartItems.length) {
      setLandCartItems(validItems);
    }
  };

  /**
   * Purchases a single land deed
   * @param {Object} item - The land deed to purchase
   * @param {string} currency - The currency for the purchase ('DEC' or 'CREDITS')
   */
  const buyLandDeed = async (item, currency) => {
    return await buyLandDeeds([item], currency);
  };

  /**
   * Purchases multiple land deeds in a single transaction
   * @param {Array} items - Array of land deeds to purchase
   * @param {string} currency - The currency for the purchase ('DEC' or 'CREDITS')
   */
  const buyLandDeeds = async (items, currency) => {
    try {
      setLoading(true);
      
      // Validate input
      if (!items || !Array.isArray(items) || items.length === 0) {
        throw new Error('No land deeds provided for purchase');
      }

      // Check if we have the necessary item information for all items
      for (const item of items) {
        if (!item || !item.uid) {
          throw new Error('Invalid land deed data for purchase');
        }
        
        // Validate that listingItemId exists, it's crucial for purchase transactions
        if (!item.listingItemId) {
          throw new Error(`Land deed ${item.uid} is missing required listing ID`);
        }
      }

      for (const item of items) {
        // Skip availability check - let the transaction handle any unavailable items
      }

      // Validate if user is logged in through the AuthService
      const authService = window.authService || (await import('../services/AuthService')).default;
      if (!authService.isLoggedIn()) {
        throw new Error('You must be logged in to make purchases');
      }

      const username = authService.getCurrentUser()?.username;
      if (!username) {
        throw new Error('Unable to get username for transaction');
      }
      const blockchainSettings = window.splinterlandsBlockchainSettings || 
                               await getLatestBlockchainSettings();
      
      if (!blockchainSettings || !blockchainSettings.last_block) {
        // Could not fetch blockchain settings, continuing with purchase anyway
      } else {
        // Current blockchain block: ${blockchainSettings.last_block}
      }

      // Get current prices for calculations
      const currentDecPrice = await getCurrentDecPrice();
      const creditsPrice = await getCreditsPrice();
      
      // Calculate totals and fees
      let totalUsdCost = 0;
      let totalDecEquivalent = 0;
      
      items.forEach(item => {
        const itemPrice = item.price || item.buy_price || item.listing_price || 0;
        totalUsdCost += itemPrice;
      });
      
      // Format the purchase data for land purchases
      // Simplified format using Layer 2 payload (similar to ItemDetailsDialog.jsx)
      const purchaseItems = items.map(item => {
        // Calculate estimated cost based on currency
        let estimatedCost;
        
        if (currency === 'DEC') {
          estimatedCost = convertUsdToDec(item.price || 0);
          // Add small safety buffer (1.5%) to account for DEC price fluctuations
          estimatedCost = Math.ceil(estimatedCost * 1.015); // 1.5% buffer
        } else if (currency === 'CREDITS') {
          estimatedCost = convertUsdToCredits(item.price || 0);
          // No buffer needed for CREDITS - they have a fixed rate (1000 CREDITS = 1 USD)
          estimatedCost = Math.ceil(estimatedCost);
        } else {
          estimatedCost = item.price || 0; // fallback to USD
        }
        
        return {
          listingItemId: parseInt(item.listingItemId),
          quantity: 1, // Always 1 for land deeds
          currency: currency,
          estimatedCost: estimatedCost
        };
      });
      
      // Generate nonce for uniqueness
      const nonce = 'sm' + Math.random().toString(36).substring(2, 15);
      
      // Create simplified purchase data for land market
      // This uses the new Layer 2 payload format (similar to ItemDetailsDialog.jsx)
      const purchaseData = {
        items: purchaseItems,
        market: "elmo.market", // Our market account that receives fees
        app: "elmo/1.0.0", // App identifier
        n: nonce
        // Note: Fee data is no longer needed as it's calculated by Layer 2
      };
      

    

      // Create the custom JSON operation for the Splinterlands marketplace purchase
      // Using the new Layer 2 standard format
      const customJsonData = {
        id: "sm_marketplace_purchase",
        json: JSON.stringify(purchaseData),
        required_auths: [username],
        required_posting_auths: []
      };

      // Execute the transaction with Hive Keychain
      return new Promise((resolve, reject) => {
        if (!window.hive_keychain) {
          reject(new Error('Hive Keychain extension not found'));
          return;
        }
        
        // Execute the keychain transaction
        window.hive_keychain.requestCustomJson(
          username,
          "sm_marketplace_purchase",
          'Active', // Active key is required for market purchases
          customJsonData.json,
          'Purchase Land Deed',
          (response) => {
            if (response.success) {
              
              // Remove purchased items from cart
              const purchasedIds = items.map(item => item.uid);
              setLandCartItems(prev => prev.filter(item => !purchasedIds.includes(item.uid)));
              
              resolve({
                success: true,
                transactionId: response.result.id,
                message: `Successfully purchased ${items.length} land deed(s)`,
                items: items
              });
            } else {
              
              // Enhanced error handling with specific messages
              let errorMessage = response.error || 'Transaction failed';
              
              // Check for common Splinterlands marketplace errors
              if (errorMessage.includes('cancelled or completed')) {
                errorMessage = 'This land deed has already been sold or is no longer available. Please refresh the page and try again.';
              } else if (errorMessage.includes('Error loading 2nd layer transaction data')) {
                errorMessage = 'Splinterlands is experiencing high traffic. Please wait a moment and try again.';
              } else if (errorMessage.includes('insufficient')) {
                errorMessage = 'Insufficient balance to complete this purchase.';
              } else if (errorMessage.includes('not found')) {
                errorMessage = 'Land deed not found. It may have been sold already.';
              }
              
              reject(new Error(errorMessage));
            }
            setLoading(false);
          }
        );
      });
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  // The value provided to the context
  const contextValue = {
    landCartItems,
    addToLandCart,
    removeFromLandCart,
    clearLandCart,
    buyLandDeed,
    buyLandDeeds,
    cleanupLandCart,
    loading
  };

  return (
    <LandCartContext.Provider value={contextValue}>
      {children}
    </LandCartContext.Provider>
  );
};

export default LandCartContext;
